// services/budgetApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ApiReportsResponse,
  GetCoinsStatisticsResponse,
  ApiReportDetailsResponse,
} from '../../types/reports';
import { baseQueryWithReauth } from './baseQuery';
import { DisputeItem } from '@/components/reports/dispute/RequestsTable';
import Helpers from '@/lib/helpers';
import { DisputeDetailsData } from '@/components/reports/dispute/RequestDetailsDrawer';
import { OrderDetailsReportData } from '@/components/reports/orders/RequestDetailsDrawer';
import { OrderReportType } from '@/components/reports/orders/RequestsTable';
import { OngoingReportType } from '@/components/reports/OrdersBudget/RequestsTable';
import { UserProfileItem } from '@/components/reports/users/RequestsTable';
import { UserDetailsReport } from '@/components/reports/users/RequestDetailsDrawer';

import {
  setReportPeriod,
  updateReportDateFilters,
} from '@/store/slices/reportFiltersSlice';
import { TotalRevenueItemType } from '@/components/reports/totalRevenue/RequestsTable';
import { TotalRevenueDetailsType } from '@/components/reports/totalRevenue/RequestDetailsDrawer';
import { OngoingOrdersReportType } from '@/components/reports/OrdersBudget/RequestDetailsDrawer';
import dayjs from 'dayjs';
import { PeriodKey, periodMapper } from '@/components/reports/SharedComponents/DatesFilters';

export const reportsService = createApi({
  reducerPath: 'reportsService',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    // ---------------------------------------------------------- Lookups
    getDisputeRequesterRolesLK: builder.query<
      { id: string; name: string }[],
      void
    >({
      query: () => ({
        url: `/dispute-service/v1/requester-role`,
      }),
      transformResponse: (
        rawResult:
          | { payload?: { id: string; name: string }[] }
          | { id: string; name: string }[]
      ) => {
        if (Array.isArray(rawResult)) return rawResult;
        return rawResult?.payload || [];
      },
    }),
    getOrderMethodLK: builder.query<{ id: string; name: string }[], void>({
      query: () => ({
        url: `/lookup-service/v1/order-methods`,
      }),
    }),
    getTotalRevenueRequestTypesLK: builder.query<
      { id: string; name: string }[],
      void
    >({
      query: () => ({
        url: `/payment-service/v1/total-revenue/request-types`,
      }),
      transformResponse: (
        rawResult:
          | { payload?: { id: string; name: string }[] }
          | { id: string; name: string }[]
      ) => {
        if (Array.isArray(rawResult)) return rawResult;
        return rawResult?.payload || [];
      },
    }),
    getReportPeriodsLK: builder.query<
      { id: string; name: string; code: string }[],
      void
    >({
      query: () => ({
        url: `/lookup-service/v1/report-periods`,
      }),
      transformResponse: (rawResult: {
        payload?: { id: string; name: string; code: string }[];
      }) => {
        return rawResult?.payload || [];
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const periodList = Array.isArray(data) ? data : [];
          const defaultValue = periodList?.find(
            (item) => item.code === 'THREE_MONTHS'
          );

          const pastDate = dayjs()
            .subtract(periodMapper[defaultValue?.code as PeriodKey] || 30, 'day')
            .format('YYYY-MM-DD');
          const currentDate = dayjs().format('YYYY-MM-DD');

          const state = getState() as { reportFilters?: { period?: string } };
          const currentPeriod = state?.reportFilters?.period;

          if (defaultValue && !currentPeriod) {
            dispatch(setReportPeriod(defaultValue?.id));
            dispatch(
              updateReportDateFilters({
                fromDate: pastDate,
                toDate: currentDate,
              })
            );
          }
        } catch (error) {
          console.error('Failed to load report periods:', error);
        }
      },
    }),

    // ---------------------------------------------------------- Dispute Reports
    getDisputesStats: builder.query<
      GetCoinsStatisticsResponse,
      { period?: string } | Record<string, any> | void
    >({
      query: (params) => ({
        url: `/dispute-service/v1/dispute-report/stats`,
        params:
          params && typeof params === 'object' && Object.keys(params).length > 0
            ? params
            : undefined,
      }),
    }),
    getDisputesReport: builder.query<
      ApiReportsResponse<DisputeItem>,
      Record<string, string | number | boolean | undefined | string[]> | void
    >({
      query: (params) => ({
        url: `/dispute-service/v1/dispute-report`,
        params: params || undefined,
      }),
    }),
    getDisputeReportById: builder.query<
      ApiReportDetailsResponse<DisputeDetailsData>,
      string
    >({
      query: (id) => ({
        url: `/dispute-service/v1/dispute-report/${id}`,
      }),
    }),
    getDisputeReportFile: builder.query<
      void,
      { params?: Record<string, unknown> } | Record<string, unknown> | void
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const params =
          _arg && typeof _arg === 'object' && 'params' in _arg
            ? _arg.params
            : _arg || undefined;

        const result = await fetchWithBQ({
          url: '/dispute-service/v1/dispute-report/export',
          method: 'GET',
          params: params,
          responseHandler: (response) => response.blob(),
        });

        if (result.error) return { error: result.error };

        const blob = result.data as Blob;

        Helpers.downloadBlobFile({ blob, fileName: 'report.pdf' });

        return { data: undefined };
      },
    }),

    // ---------------------------------------------------------- Ongoing Orders Reports
    getOngoingOrdersStats: builder.query<
      GetCoinsStatisticsResponse,
      { period?: string } | Record<string, unknown> | void
    >({
      query: (params) => ({
        url: `/aggregation-service/v1/ongoing-orders/stats`,
        params:
          params && typeof params === 'object' && Object.keys(params).length > 0
            ? params
            : undefined,
      }),
    }),
    getOngoingOrdersReport: builder.query<
      ApiReportsResponse<OngoingReportType>,
      Record<string, string | number | boolean | undefined | string[]> | void
    >({
      query: (params) => ({
        url: `/aggregation-service/v1/ongoing-orders`,
        params: params || undefined,
      }),
    }),
    getOngoingOrdersReportById: builder.query<
      ApiReportDetailsResponse<OngoingOrdersReportType>,
      string
    >({
      query: (id) => ({
        url: `/aggregation-service/v1/ongoing-orders/${id}`,
      }),
    }),
    getOngoingOrdersReportFile: builder.query<
      void,
      { params?: Record<string, unknown> } | Record<string, unknown> | void
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const params =
          _arg && typeof _arg === 'object' && 'params' in _arg
            ? _arg.params
            : _arg || undefined;

        const result = await fetchWithBQ({
          url: '/aggregation-service/v1/ongoing-orders/export',
          method: 'GET',
          params: params,
          responseHandler: (response) => response.blob(),
        });

        if (result.error) return { error: result.error };

        const blob = result.data as Blob;

        Helpers.downloadBlobFile({ blob, fileName: 'report.pdf' });

        return { data: undefined };
      },
    }),
    // ---------------------------------------------------------- Orders Reports
    getOrdersStats: builder.query<
      GetCoinsStatisticsResponse,
      { period?: string } | Record<string, unknown> | void
    >({
      query: (params) => ({
        url: `aggregation-service/v1/order-report/stats`,
        params:
          params && typeof params === 'object' && Object.keys(params).length > 0
            ? params
            : undefined,
      }),
    }),

    getOrdersReport: builder.query<
      ApiReportsResponse<OrderReportType>,
      Record<string, string | number | boolean | undefined | string[]> | void
    >({
      query: (params) => ({
        url: `/aggregation-service/v1/order-report`,
        params: params || undefined,
      }),
    }),
    getOrdersReportById: builder.query<
      ApiReportDetailsResponse<OrderDetailsReportData>,
      string
    >({
      query: (id) => ({
        url: `/aggregation-service/v1/order-report/${id}`,
      }),
    }),
    getOrdersReportFile: builder.query<
      void,
      { params?: Record<string, unknown> } | Record<string, unknown> | void
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const params =
          _arg && typeof _arg === 'object' && 'params' in _arg
            ? _arg.params
            : _arg || undefined;

        const result = await fetchWithBQ({
          url: '/aggregation-service/v1/order-report/export',
          method: 'GET',
          params: params,
          responseHandler: (response) => response.blob(),
        });

        if (result.error) return { error: result.error };

        const blob = result.data as Blob;

        Helpers.downloadBlobFile({ blob, fileName: 'report.pdf' });

        return { data: undefined };
      },
    }),

    // ---------------------------------------------------------- Users Reports
    getUsersStats: builder.query<
      GetCoinsStatisticsResponse,
      { period?: string } | Record<string, unknown> | void
    >({
      query: (params) => ({
        url: `/aggregation-service/v1/user-report/stats`,
        params:
          params && typeof params === 'object' && Object.keys(params).length > 0
            ? params
            : undefined,
      }),
    }),

    getUserssReport: builder.query<
      ApiReportsResponse<UserProfileItem>,
      Record<string, string | number | boolean | undefined | string[]> | void
    >({
      query: (params) => ({
        url: `/aggregation-service/v1/user-report`,
        params: params || undefined,
      }),
    }),
    getUserReportById: builder.query<
      ApiReportDetailsResponse<UserDetailsReport>,
      string
    >({
      query: (id) => ({
        url: `/aggregation-service/v1/user-report/${id}`,
      }),
    }),
    getUsersReportFile: builder.query<
      void,
      { params?: Record<string, any> } | Record<string, any> | void
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const params =
          _arg && typeof _arg === 'object' && 'params' in _arg
            ? _arg.params
            : _arg || undefined;

        const result = await fetchWithBQ({
          url: '/aggregation-service/v1/user-report/export',
          method: 'GET',
          params: params,
          responseHandler: (response) => response.blob(),
        });

        if (result.error) return { error: result.error };

        const blob = result.data as Blob;

        Helpers.downloadBlobFile({ blob, fileName: 'report.pdf' });

        return { data: undefined };
      },
    }),
    // ---------------------------------------------------------- Revenue
    getTotalRevenueStats: builder.query<
      GetCoinsStatisticsResponse,
      { period?: string } | Record<string, unknown> | void
    >({
      query: (params) => ({
        url: `/payment-service/v1/total-revenue/stats`,
        params:
          params && typeof params === 'object' && Object.keys(params).length > 0
            ? params
            : undefined,
      }),
    }),

    getTotalRevenueReport: builder.query<
      ApiReportsResponse<TotalRevenueItemType>,
      Record<string, string | number | boolean | undefined | string[]> | void
    >({
      query: (params) => ({
        url: `/payment-service/v1/total-revenue`,
        params: params || undefined,
      }),
    }),
    getTotalRevenueReportById: builder.query<
      ApiReportDetailsResponse<TotalRevenueDetailsType>,
      string
    >({
      query: (id) => ({
        url: `/payment-service/v1/total-revenue/${id}`,
      }),
    }),
    getTotalRevenueReportFile: builder.query<
      void,
      { params?: Record<string, any> } | Record<string, any> | void
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const params =
          _arg && typeof _arg === 'object' && 'params' in _arg
            ? _arg.params
            : _arg || undefined;

        const result = await fetchWithBQ({
          url: '/payment-service/v1/total-revenue/export',
          method: 'GET',
          params: params,
          responseHandler: (response) => response.blob(),
        });

        if (result.error) return { error: result.error };

        const blob = result.data as Blob;

        Helpers.downloadBlobFile({ blob, fileName: 'report.pdf' });

        return { data: undefined };
      },
    }),
    // ---------------------------------------------------------- Subscription Reports
    getSubscriptionPeriodTypesLK: builder.query<
      { id: string; name: string }[],
      void
    >({
      query: () => ({
        url: `/marketplace-service/v1/period-type`,
      }),
      transformResponse: (
        rawResult:
          | { payload?: { id: string; name: string }[] }
          | { id: string; name: string }[]
      ) => {
        if (Array.isArray(rawResult)) return rawResult;
        return rawResult?.payload || [];
      },
    }),
    getSubscriptionPlansLK: builder.query<
      { id: string; name: string }[],
      void
    >({
      query: () => ({
        url: `/marketplace-service/v1/operator/marketplace-plans/lookup`,
      }),
      transformResponse: (
        rawResult:
          | { payload?: { id: string; name: string }[] }
          | { id: string; name: string }[]
      ) => {
        if (Array.isArray(rawResult)) return rawResult;
        return rawResult?.payload || [];
      },
    }),
    getSubscriptionStats: builder.query<
      GetCoinsStatisticsResponse,
      { period?: string } | Record<string, unknown> | void
    >({
      query: (params) => ({
        url: `/aggregation-service/v1/operator/subscription-report/stats`,
        params:
          params && typeof params === 'object' && Object.keys(params).length > 0
            ? params
            : undefined,
      }),
    }),
    getSubscriptionReport: builder.query<
      ApiReportsResponse<any>,
      Record<string, string | number | boolean | undefined | string[]> | void
    >({
      query: (params) => {
        const cleanParams =
          params && typeof params === 'object'
            ? Object.fromEntries(
                Object.entries(params).filter(
                  ([_, v]) => v !== undefined && v !== null && v !== ''
                )
              )
            : undefined;
        return {
          url: `/aggregation-service/v1/operator/subscription-report`,
          params: cleanParams,
        };
      },
    }),
    getSubscriptionReportById: builder.query<
      ApiReportDetailsResponse<any>,
      string
    >({
      query: (id) => ({
        url: `/aggregation-service/v1/operator/subscription-report/profiles/${id}`,
      }),
    }),
    getSubscriptionReportFile: builder.query<
      void,
      { params?: Record<string, any> } | Record<string, any> | void
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const params =
          _arg && typeof _arg === 'object' && 'params' in _arg
            ? _arg.params
            : _arg || undefined;

        const result = await fetchWithBQ({
          url: '/aggregation-service/v1/operator/subscription-report/export',
          method: 'GET',
          params: params,
          responseHandler: (response) => response.blob(),
        });

        if (result.error) return { error: result.error };

        const blob = result.data as Blob;

        Helpers.downloadBlobFile({ blob, fileName: 'report.pdf' });

        return { data: undefined };
      },
    }),
  }),
});

export const {
  useGetDisputeRequesterRolesLKQuery,
  useGetDisputesStatsQuery,
  useGetReportPeriodsLKQuery,
  useGetDisputesReportQuery,
  useGetDisputeReportByIdQuery,
  useLazyGetDisputeReportFileQuery,
  useGetOrderMethodLKQuery,
  useGetOngoingOrdersStatsQuery,
  useGetOngoingOrdersReportQuery,
  useGetOngoingOrdersReportByIdQuery,
  useLazyGetOngoingOrdersReportFileQuery,
  useGetOrdersStatsQuery,
  useGetOrdersReportQuery,
  useGetOrdersReportByIdQuery,
  useLazyGetOrdersReportFileQuery,
  useGetUsersStatsQuery,
  useGetUserssReportQuery,
  useGetUserReportByIdQuery,
  useLazyGetUsersReportFileQuery,
  useGetTotalRevenueStatsQuery,
  useGetTotalRevenueReportQuery,
  useGetTotalRevenueReportByIdQuery,
  useLazyGetTotalRevenueReportFileQuery,
  useGetTotalRevenueRequestTypesLKQuery,
  useGetSubscriptionPeriodTypesLKQuery,
  useGetSubscriptionPlansLKQuery,
  useGetSubscriptionStatsQuery,
  useGetSubscriptionReportQuery,
  useGetSubscriptionReportByIdQuery,
  useLazyGetSubscriptionReportFileQuery,
} = reportsService;
